import { z } from 'zod';

// Contrato de la etapa de capacitación de un documento (G4, cierre F1): GET
// /api/v1/quality/documents/:entryId/training-stage. Validado con Zod: una forma
// inesperada cae al error de la tarjeta, nunca a una excepción en el render.

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const TRAINING_STATES = [
  'not_started',
  'in_progress',
  'completed',
  'not_applicable',
  'superseded',
  'retired',
  // G2-C: Calidad la cerró sin el reporte del LMS (no es completada).
  'closed_by_quality',
  'unknown',
] as const;

export const trainingStageSchema = z.object({
  state: z.enum(TRAINING_STATES),
  /** Versión a la que se refiere la etapa (puede ser la vigente mientras se elabora otra). */
  version: z.number().int().positive().nullable(),
  openedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  dueDate: day.nullable(),
  overdue: z.boolean(),
  progress: z
    .object({ assigned: z.number().int().nonnegative(), completed: z.number().int().nonnegative() })
    .nullable(),
  suggestedEffectiveDate: day,
  canReschedule: z.boolean(),
  /** G2-D: el documento está vinculado al LMS (sin vínculo no hay a quién capacitar). */
  lmsLinked: z.boolean(),
  /** Primera vigencia que admite la reprogramación (hoy solo con la activación en el acto). */
  rescheduleMinDate: day,
  // G2-C: desde cuándo el LMS no reporta la capacitación abierta, y por qué.
  lmsUnreportedSince: z.string().nullable(),
  lmsUnreportedReason: z.enum(['missing', 'version_mismatch']).nullable(),
  canCloseTraining: z.boolean(),
});
export type TrainingStage = z.infer<typeof trainingStageSchema>;

export const trainingStageResponseSchema = z.object({
  success: z.literal(true),
  data: trainingStageSchema,
});

/**
 * G2-D: vigencia sugerida al firmar (hoy + plazo de capacitación), solo si el documento
 * está vinculado al LMS. Sin vínculo no hay lecturas que venzan: no se sugiere nada.
 */
export function suggestedApprovalDate(t: TrainingStage | undefined): string | null {
  return t?.lmsLinked === true ? t.suggestedEffectiveDate : null;
}

/**
 * La etapa solo si es de la versión `version` (la del flujo del editor). Mientras se
 * elabora una modificación, la capacitación abierta de la versión vigente no se mezcla
 * con el stepper de la nueva. Sin versión de un lado o del otro, no hay nada que comparar.
 */
export function trainingForVersion(
  t: TrainingStage | null,
  version: number | null,
): TrainingStage | null {
  if (t === null || version === null || t.version === null) {
    return t;
  }
  return t.version === version ? t : null;
}
